import { Download, FolderArchive, Paperclip, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAppStore } from "@/store/app";
import {
  useGenerateSynthetic,
  useUploadDocument,
} from "@/hooks/api";
import { ensureSession } from "@/lib/session";
import type { DocumentKind } from "@/api/types";

function token() {
  return Math.random().toString(36).slice(2);
}

export function EmptyState() {
  const selectDoc = useAppStore((s) => s.selectDoc);
  const addPending = useAppStore((s) => s.addPending);
  const removePending = useAppStore((s) => s.removePending);
  const openAppealTypeDialog = useAppStore((s) => s.openAppealTypeDialog);
  const pendingCount = useAppStore((s) => s.pendingEntries.length);
  const { generateSynthetic, isPending: genPending, pendingKind: genKind } =
    useGenerateSynthetic();
  const { uploadDocument, isPending: upPending, pendingKind: upKind } =
    useUploadDocument();

  const anyBusy = genPending || upPending || pendingCount > 0;
  const busyKind: DocumentKind | null = genKind ?? upKind ?? null;

  async function generateClaim() {
    const t = token();
    addPending({ token: t, kind: "edi_837", mode: "synthesize" });
    try {
      const sid = await ensureSession();
      const parsed = await generateSynthetic({ sessionId: sid, kind: "edi_837" });
      selectDoc(parsed.info.id);
      toast.success(`Generated ${parsed.info.filename}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Generation failed: ${msg}`);
    } finally {
      removePending(t);
    }
  }

  async function browseAndUpload(kind: DocumentKind) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = kind === "edi_837" ? ".edi,.txt,.dat" : ".pdf";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const t = token();
      addPending({ token: t, kind, mode: "upload" });
      try {
        const sid = await ensureSession();
        const parsed = await uploadDocument({ sessionId: sid, kind, file });
        selectDoc(parsed.info.id);
        toast.success(`Uploaded ${parsed.info.filename}`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Upload failed: ${msg}`);
      } finally {
        removePending(t);
      }
    };
    input.click();
  }

  return (
    <main
      className="flex items-center justify-center bg-slate-50"
      style={{ minHeight: "calc(100vh - var(--chrome-h, 56px))" }}
    >
      <div className="w-[560px] bg-white rounded-2xl border shadow-sm px-10 py-14 text-center -translate-y-[6vh]">
        <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
          <Download className="w-8 h-8 text-slate-700" strokeWidth={1.6} />
        </div>

        <h1 className="mt-6 text-[22px] font-semibold tracking-tight">Start with a claim</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Drop a real <span className="font-medium text-foreground">837</span> or{" "}
          <span className="font-medium text-foreground">UB04</span> anywhere on the page, or
          generate a synthetic one to explore the demo.
        </p>

        <div className="mt-7 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            disabled={anyBusy}
            onClick={() => browseAndUpload("edi_837")}
            className="gap-2"
          >
            <Paperclip className="w-4 h-4" />
            {upPending && busyKind === "edi_837" ? "Uploading…" : "Browse 837"}
          </Button>
          <Button
            variant="outline"
            disabled={anyBusy}
            onClick={() => browseAndUpload("ub04")}
            className="gap-2"
          >
            <Paperclip className="w-4 h-4" />
            {upPending && busyKind === "ub04" ? "Uploading…" : "Browse UB04"}
          </Button>
        </div>

        <div className="mt-7 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400">
          <span className="flex-1 h-px bg-slate-200" />
          <span>or</span>
          <span className="flex-1 h-px bg-slate-200" />
        </div>

        <div className="mt-7 flex items-center justify-center gap-2 flex-wrap">
          <Button size="lg" className="gap-2" disabled={anyBusy} onClick={generateClaim}>
            <Wand2 className="w-4 h-4" />
            {genPending && busyKind === "edi_837" ? "Generating…" : "Generate synthetic claim"}
          </Button>

          <Button
            size="lg"
            variant="outline"
            className="gap-2"
            disabled={anyBusy}
            onClick={() => openAppealTypeDialog({ mode: "bundle" })}
          >
            <FolderArchive className="w-4 h-4" />
            Try sample data
          </Button>
        </div>

        <p className="mt-5 text-[11px] text-muted-foreground">
          First doc must be 837 or UB04. Synthetic generation locks the session.
        </p>
      </div>
    </main>
  );
}
